import { useEffect, useId, useMemo, useRef, useState } from "react";
import { api } from "../api";
import type { CaseRecord, Direction, SourceType } from "../types";
import ModalBackdrop from "./ui/ModalBackdrop";

// Manual record-creation modal. Rendered from the Add menu's "Write
// record" option for phone calls, in-person conversations, handwritten
// notes, etc. — everything that doesn't come in as a digital file.
//
// Submit path:
//   1. If a file is attached, POST to /api/cases/:caseId/files first
//      (same endpoint file-upload uses). The server extracts OCR/text
//      and returns a handle.
//   2. POST to /api/cases/:caseId/records with the form fields +
//      creation_source: "manual" and (optionally) the file handle.
//   3. Parent's onRecordCreated adds the new record to the in-memory
//      list and the modal closes.
//   4. Parent kicks off a background scoreAll so the new record picks
//      up significance/key-fact scoring in parallel with the UI toast.

const RECORD_TYPES = [
  { value: "phone_call", label: "Phone call", emoji: "☎" },
  { value: "letter", label: "Letter", emoji: "✉" },
  { value: "in_person", label: "In-person conversation", emoji: "👥" },
  { value: "other", label: "Note", emoji: "✏" }, // "note" maps to source_type=other
  { value: "other_explicit", label: "Other", emoji: "●" },
] as const;
type RecordTypeValue = (typeof RECORD_TYPES)[number]["value"];

// Maps the form's record-type picker to the worker's SourceType.
// "Note" and "Other" both land on "other" server-side — they're the
// same for the backend's purposes — but the UI keeps them distinct
// for clarity in the dropdown.
function toSourceType(v: RecordTypeValue): SourceType {
  if (v === "other_explicit") return "other";
  return v as SourceType;
}

interface Props {
  caseId: string;
  existingRecords: CaseRecord[];
  onClose: () => void;
  onRecordCreated: (r: CaseRecord) => void;
}

function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function WriteRecordModal({
  caseId,
  existingRecords,
  onClose,
  onRecordCreated,
}: Props) {
  const [date, setDate] = useState(todayYmd());
  const [recordType, setRecordType] = useState<RecordTypeValue>("phone_call");
  const [direction, setDirection] = useState<Direction>("inbound");
  const [subject, setSubject] = useState("");
  const [fromAddr, setFromAddr] = useState("");
  const [toAddr, setToAddr] = useState("");
  const [body, setBody] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<{
    subject?: boolean;
    body?: boolean;
    date?: boolean;
  }>({});

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Datalist ids need to be unique on the page — multiple modals of
  // this component are rare but the hook guarantees stability.
  const fromListId = useId();
  const toListId = useId();

  // Unique-ish known-party suggestions, pulled from existing record
  // from/to addresses. Casts to Array so we can sort. Short suggestions
  // (<3 chars) are dropped — they're usually noise like "me" or "n/a".
  const knownParties = useMemo(() => {
    const s = new Set<string>();
    for (const r of existingRecords) {
      if (r.from_addr && r.from_addr.trim().length >= 3)
        s.add(r.from_addr.trim());
      if (r.to_addr && r.to_addr.trim().length >= 3) s.add(r.to_addr.trim());
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [existingRecords]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !submitting) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }

  function validate(): string | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return "Pick a valid date.";
    if (!subject.trim()) return "Subject is required.";
    if (!body.trim()) return "Body is required.";
    return null;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched({ subject: true, body: true, date: true });
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Optional file attach: run it through the same pipeline that
      // file-upload uses so we get OCR/text extraction "for free".
      let fileMeta: {
        file_r2_key?: string | null;
        file_name?: string | null;
        file_mime_type?: string | null;
        ocr_text?: string | null;
      } = {};
      if (file) {
        const upload = await api.uploadFile(caseId, file);
        fileMeta = {
          file_r2_key: upload.file_r2_key,
          file_name: upload.file_name,
          file_mime_type: upload.file_mime_type,
          ocr_text: upload.extracted.ocr_text ?? null,
        };
      }

      const tagList = tagsInput
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter((t) => t.length > 0);

      // Noon UTC per the timezone convention — matches the repair
      // endpoint and file-upload paths so the Records list renders
      // the same calendar day in any locale.
      const recordDateIso = `${date}T12:00:00.000Z`;

      const { record } = await api.createRecord(caseId, {
        record_date: recordDateIso,
        direction,
        source_type: toSourceType(recordType),
        subject: subject.trim(),
        from_addr: fromAddr.trim() || null,
        to_addr: toAddr.trim() || null,
        body_text: body.trim(),
        tags: tagList.length > 0 ? tagList.join(",") : null,
        creation_source: "manual",
        // User-entered date is authoritative — the file (if any)
        // doesn't get extracted into record_date for manual records.
        date_source: "manual",
        ...fileMeta,
      });

      // A true content duplicate is dropped server-side (no record returned).
      if (record) onRecordCreated(record);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  const subjectInvalid = touched.subject && !subject.trim();
  const bodyInvalid = touched.body && !body.trim();
  const dateInvalid = touched.date && !/^\d{4}-\d{2}-\d{2}$/.test(date);

  return (
    <ModalBackdrop onClose={submitting ? null : onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="write-record-title"
        className="write-record-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="write-record-header">
          <h2 id="write-record-title" className="modal-title">
            Write record
          </h2>
          <button
            type="button"
            className="btn-icon"
            onClick={onClose}
            aria-label="Close"
            disabled={submitting}
          >
            ✕
          </button>
        </div>

        <form className="write-record-form" onSubmit={onSubmit}>
          <div className="write-record-grid">
            <label className="write-record-field">
              <span className="write-record-label">Date</span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, date: true }))}
                required
                className={dateInvalid ? "invalid" : ""}
                disabled={submitting}
              />
            </label>
            <label className="write-record-field">
              <span className="write-record-label">Record type</span>
              <select
                value={recordType}
                onChange={(e) =>
                  setRecordType(e.target.value as RecordTypeValue)
                }
                disabled={submitting}
              >
                {RECORD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.emoji}  {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="write-record-field">
              <span className="write-record-label">Direction</span>
              <select
                value={direction}
                onChange={(e) => setDirection(e.target.value as Direction)}
                disabled={submitting}
              >
                <option value="inbound">Inbound (I received this)</option>
                <option value="outbound">Outbound (I sent this)</option>
              </select>
            </label>
          </div>

          <label className="write-record-field">
            <span className="write-record-label">
              Subject <span className="write-record-required">*</span>
            </span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, subject: true }))}
              placeholder="Brief description"
              required
              className={subjectInvalid ? "invalid" : ""}
              disabled={submitting}
            />
          </label>

          <div className="write-record-grid">
            <label className="write-record-field">
              <span className="write-record-label">From</span>
              <input
                type="text"
                value={fromAddr}
                onChange={(e) => setFromAddr(e.target.value)}
                placeholder="Who communicated this"
                list={fromListId}
                disabled={submitting}
              />
            </label>
            <label className="write-record-field">
              <span className="write-record-label">To</span>
              <input
                type="text"
                value={toAddr}
                onChange={(e) => setToAddr(e.target.value)}
                placeholder="Who received it"
                list={toListId}
                disabled={submitting}
              />
            </label>
          </div>
          {/* Shared datalist of known-party suggestions for both inputs */}
          <datalist id={fromListId}>
            {knownParties.map((p) => (
              <option key={"f-" + p} value={p} />
            ))}
          </datalist>
          <datalist id={toListId}>
            {knownParties.map((p) => (
              <option key={"t-" + p} value={p} />
            ))}
          </datalist>

          <label className="write-record-field">
            <span className="write-record-label">
              Body <span className="write-record-required">*</span>
            </span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, body: true }))}
              placeholder="What was said or communicated — include key details, quotes, and outcomes"
              required
              className={`write-record-textarea${bodyInvalid ? " invalid" : ""}`}
              disabled={submitting}
            />
          </label>

          <label className="write-record-field">
            <span className="write-record-label">Tags</span>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="Comma-separated — e.g. urgent, follow-up"
              disabled={submitting}
            />
          </label>

          <div className="write-record-field">
            <span className="write-record-label">Attachment (optional)</span>
            <div
              className={`write-record-dropzone${
                isDragOver ? " drag-over" : ""
              }${file ? " has-file" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setFile(f);
                }}
                style={{ display: "none" }}
                disabled={submitting}
              />
              {file ? (
                <>
                  <span>
                    📎 {file.name} ·{" "}
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      if (fileInputRef.current)
                        fileInputRef.current.value = "";
                    }}
                    disabled={submitting}
                  >
                    Remove
                  </button>
                </>
              ) : (
                <span>
                  Drop a file here, or click to browse — scan, photo, or
                  document related to this record
                </span>
              )}
            </div>
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="write-record-footer">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? "Creating record…" : "Create record"}
            </button>
          </div>
        </form>
      </div>
    </ModalBackdrop>
  );
}
